import {
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconBuilding,
  IconExternalLink,
  IconFileCertificate,
  IconLayoutDashboard,
  IconLink,
  IconMapPin,
  IconPackage,
  IconSearch,
  IconShoppingCart,
  IconTicket,
  IconUsers,
  IconWorldWww,
  IconWorld,
} from '@tabler/icons-react';
import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useTicketCockpit } from '../../stores/ticketCockpit';
import { useCustomerDrawerStore } from '../../stores/customerDrawerStore';
import { WeclappTicketCockpit } from '../../components/WeclappTicketCockpit';
import { MaterialHistoryTab } from './MaterialHistoryTab';
import { CustomerDnsZonesTab } from './CustomerDnsZonesTab';
import { CustomerDomainsTab } from './CustomerDomainsTab';
import { ContractCareCard, OpeningHoursCard, PortfolioCard, ServiceNotesCard } from './CustomerCareCards';
import { CustomerOpeningStatusBadge } from '../../components/customer/openingHours/CustomerOpeningStatusBadge';
import { CustomerContactsPanel } from './CustomerContactsPanel';
import { CustomerDeadlinesSection } from './CustomerDeadlinesSection';
import { InfoCard, InfoEmpty } from './CustomerInfoParts';
import { useCustomerCare, useCustomerDeadlines, type CustomerInfoIdent } from './customerInfoQueries';
import type { HubAddressTag, HubCustomerContext, HubProvider, TechnicianWorkflowKind, TechnicianWorkflowStatus } from '../../lib/api/domains/technicianHub';
import type { CWTicket } from '../../lib/api/types';
import type { WeclappTicket } from '../../lib/api/types/weclapp';
import { ADDRESS_TAG_LABELS, ADDRESS_TAG_COLORS } from './addressTags';
import { weclappStatusColor } from '../../lib/ticketStatusColor';
import { needsCustomerAction } from '../../lib/customerDeadlines';
import classes from './CustomerInfo.module.css';

interface Props {
  opened: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  provider: HubProvider;
  context: HubCustomerContext | null;
  externalUiBase: string | null;
}

const KIND_LABELS: Record<TechnicianWorkflowKind, string> = {
  QUOTE_REQUEST: 'Angebotsanforderung',
  WARRANTY_CHECK: 'Garantieprüfung',
  MATERIAL_ORDER: 'Bestellanforderung',
  CUSTOMER_ORDER: 'Bestellung für Kunden',
};

const KIND_ROUTES: Record<TechnicianWorkflowKind, string> = {
  QUOTE_REQUEST: '/customer-hub/quote',
  WARRANTY_CHECK: '/customer-hub/warranty',
  MATERIAL_ORDER: '/customer-hub/order',
  CUSTOMER_ORDER: '/customer-hub/bestellung',
};

const STATUS_LABELS: Record<TechnicianWorkflowStatus, string> = {
  DRAFT: 'Entwurf',
  SUBMITTED: 'Eingereicht',
  PENDING_CUSTOMER_APPROVAL: 'Warte auf Freigabe',
  CUSTOMER_APPROVED: 'Freigegeben',
  CW_TICKET_CREATED: 'Ticket erstellt',
  FAILED: 'Fehler',
};

const STATUS_COLORS: Record<TechnicianWorkflowStatus, string> = {
  DRAFT: 'gray',
  SUBMITTED: 'blue',
  PENDING_CUSTOMER_APPROVAL: 'yellow',
  CUSTOMER_APPROVED: 'green',
  CW_TICKET_CREATED: 'teal',
  FAILED: 'red',
};

/** Standorte über dieser Zahl klappen auf „Alle anzeigen" zusammen — die Übersicht soll auf einen Blick passen. */
const SITE_PREVIEW = 4;

/** Ab so vielen Tickets lohnt die Suche. */
const TICKET_SEARCH_FROM = 8;

function formatDate(iso?: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Klickbare Listenzeile — per Tastatur genauso bedienbar wie mit der Maus. */
function rowActivation(onActivate: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
  };
}

function countBadge(value: number, color = 'gray'): ReactNode {
  return value > 0 ? (
    <Badge size="xs" variant="light" color={color} radius="sm">
      {value}
    </Badge>
  ) : undefined;
}

/** Äußere Hülle einer Liste im Reiter — dieselbe Fläche wie die Karten der Übersicht. */
function ListSurface({ children }: { children: ReactNode }) {
  return (
    <Paper withBorder radius="md" style={{ overflow: 'hidden', backgroundColor: 'var(--mantine-color-default)' }}>
      {children}
    </Paper>
  );
}

function CwTicketRow({ ticket, onClick }: { ticket: CWTicket; onClick: () => void }) {
  return (
    <div className={`${classes.row} ${classes.clickable}`} {...rowActivation(onClick)}>
      <Group justify="space-between" wrap="nowrap" gap="sm" align="flex-start">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Text size="xs" c="dimmed" ff="monospace" style={{ flexShrink: 0, marginTop: 2 }}>
              #{ticket.id}
            </Text>
            <Text size="sm" fw={500} style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
              {ticket.summary}
            </Text>
          </Group>
          {(ticket.board?.name || ticket.contactName) && (
            <Text size="xs" c="dimmed" mt={2}>
              {[ticket.board?.name, ticket.contactName].filter(Boolean).join(' · ')}
            </Text>
          )}
        </div>
        <Group gap={4} style={{ flexShrink: 0 }} wrap="wrap" justify="flex-end">
          {ticket.priority?.name && (
            <Badge size="xs" variant="light" color="orange">
              {ticket.priority.name}
            </Badge>
          )}
          {ticket.status?.name && (
            <Badge size="xs" variant="light" color="blue">
              {ticket.status.name}
            </Badge>
          )}
        </Group>
      </Group>
    </div>
  );
}

function WeclappTicketRow({ ticket, onClick }: { ticket: WeclappTicket; onClick: () => void }) {
  // Zweizeilig statt einer überbreiten Zeile: Betreff darf umbrechen, Status/Priorität/
  // Bearbeiter stehen darunter — kein horizontales Scrollen, nichts abgeschnitten.
  return (
    <div className={`${classes.row} ${classes.clickable}`} {...rowActivation(onClick)}>
      <Stack gap={4}>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <Text size="xs" c="dimmed" ff="monospace" style={{ flexShrink: 0, marginTop: 2 }}>
            #{ticket.ticketNumber}
          </Text>
          <Text size="sm" fw={500} style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {ticket.subject}
          </Text>
        </Group>
        <Group gap={6} wrap="wrap">
          {ticket.status && (
            <Badge size="xs" variant="light" color={weclappStatusColor(ticket.status)}>
              {ticket.status}
            </Badge>
          )}
          {ticket.priority && (
            <Badge size="xs" variant="light" color="orange">
              {ticket.priority}
            </Badge>
          )}
          {ticket.responsibleUserName && (
            <Text size="xs" c="dimmed">
              {ticket.responsibleUserName}
            </Text>
          )}
          {ticket.createdDate && (
            <Text size="xs" c="dimmed">
              · {new Date(ticket.createdDate).toLocaleDateString('de-DE')}
            </Text>
          )}
        </Group>
      </Stack>
    </div>
  );
}

export function CustomerInfoModal({ opened, onClose, customerId, customerName, provider, context, externalUiBase }: Props) {
  const isWeclapp = provider === 'weclapp';
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(customerId);
  const isMobile = useMediaQuery('(max-width: 48em)') ?? false;

  const navigate = useNavigate();
  const { open: openCockpit } = useTicketCockpit();
  const [weclappCockpitId, setWeclappCockpitId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const [ticketSearch, setTicketSearch] = useState('');
  const [showAllSites, setShowAllSites] = useState(false);

  // Anderer Kunde → wieder bei der Übersicht anfangen, nicht im Reiter des vorigen.
  useEffect(() => {
    setActiveTab('overview');
    setTicketSearch('');
    setShowAllSites(false);
  }, [customerId]);

  // Schrittweise Rück-Navigation in der Kette Kunde → Ticket → Kundenname:
  // Jeder openDrawer-Aufruf (Klick auf einen Kundennamen, auch im verschachtelten
  // Cockpit) erhöht requestSeq — dann schließt das Modal sein Cockpit (650) und
  // liegt selbst (600) wieder vorn. Schließen des Modals ist der nächste Schritt zurück.
  const drawerRequestSeq = useCustomerDrawerStore((s) => s.requestSeq);
  useEffect(() => {
    setWeclappCockpitId(null);
  }, [drawerRequestSeq]);

  // Resolve effective IDs: prefer context (available for UUID-based customers) over raw customerId
  const cwCompanyId = !isWeclapp
    ? (context?.company?.id && context.company.id > 0
        ? context.company.id
        : (!isUuid ? Number(customerId) : undefined))
    : undefined;
  // Weclapp-Kunden-IDs sind UUIDs — customerId ist in diesem Fall direkt die Weclapp-ID
  const weclappId = isWeclapp
    ? (context?.weclappCustomerId ?? customerId)
    : undefined;
  // Lokale Customer-UUID (aus context.localCustomer) für Material-Historie
  const localCustomerId = context?.localCustomer?.id ?? (isUuid ? customerId : undefined);
  const ident: CustomerInfoIdent = { localCustomerId, weclappCustomerId: weclappId, cwCompanyId };

  // Dieselben Schlüssel wie in den Reitern: die Zähler an den Reitern kosten keine eigene Abfrage.
  const care = useCustomerCare(ident, { enabled: opened });
  const deadlines = useCustomerDeadlines(ident, { enabled: opened });

  const { data: sitesData, isLoading: sitesLoading } = useQuery({
    queryKey: ['cw-company-sites', cwCompanyId],
    queryFn: () => api.getCompanySites(cwCompanyId!),
    enabled: opened && !isWeclapp && !!cwCompanyId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: cwTicketsData, isLoading: cwTicketsLoading } = useQuery({
    queryKey: ['customer-info-cw-tickets', cwCompanyId],
    queryFn: () => api.getOpenTickets({ companyId: cwCompanyId!, pageSize: 100, includeClosed: true }),
    enabled: opened && !isWeclapp && !!cwCompanyId,
    staleTime: 2 * 60 * 1000,
  });

  const { data: weclappTicketsData, isLoading: weclappTicketsLoading } = useQuery({
    queryKey: ['customer-info-weclapp-tickets', weclappId],
    queryFn: () => api.getWeclappTickets({ customerId: weclappId!, pageSize: 200 }),
    enabled: opened && isWeclapp && !!weclappId,
    staleTime: 2 * 60 * 1000,
  });

  const { data: workflowsData, isLoading: workflowsLoading } = useQuery({
    queryKey: ['customer-info-workflows', provider, cwCompanyId ?? weclappId ?? customerId],
    queryFn: () =>
      isWeclapp
        ? api.listTechnicianWorkflows(20, undefined, weclappId)
        : api.listTechnicianWorkflows(20, cwCompanyId),
    enabled: opened,
    staleTime: 2 * 60 * 1000,
  });

  const cwTickets = cwTicketsData?.tickets ?? [];
  const weclappTickets = weclappTicketsData?.tickets ?? [];
  const ticketsLoading = cwTicketsLoading || weclappTicketsLoading;
  const ticketCount = isWeclapp
    ? (weclappTicketsData?.totalCount ?? weclappTickets.length)
    : (cwTicketsData?.totalCount ?? cwTickets.length);
  const loadedTicketCount = isWeclapp ? weclappTickets.length : cwTickets.length;
  const ticketQuery = ticketSearch.trim().toLowerCase();
  const shownWeclappTickets = ticketQuery
    ? weclappTickets.filter((t) => `${t.ticketNumber} ${t.subject}`.toLowerCase().includes(ticketQuery))
    : weclappTickets;
  const shownCwTickets = ticketQuery
    ? cwTickets.filter((t) => `${t.id} ${t.summary}`.toLowerCase().includes(ticketQuery))
    : cwTickets;
  const shownTicketCount = isWeclapp ? shownWeclappTickets.length : shownCwTickets.length;

  const workflows = workflowsData?.workflows ?? [];

  const sites = !isWeclapp ? (sitesData?.sites ?? []) : [];
  const contextSites = context?.sites ?? [];
  const contacts = context?.contacts ?? [];
  const siteRows: { id: string | number; name: string; detail?: string; tags?: HubAddressTag[] }[] =
    !isWeclapp && sites.length > 0
      ? sites.map((site) => ({
          id: site.id,
          name: site.name,
          detail: [site.addressLine1, site.city, site.zip].filter(Boolean).join(', '),
        }))
      : contextSites.map((site) => ({ id: site.id, name: site.name, tags: site.addressType ?? [] }));
  const visibleSites = showAllSites ? siteRows : siteRows.slice(0, SITE_PREVIEW);

  const contactCount = care.data ? care.data.contacts.filter((c) => c.isActive).length : contacts.length;
  const activeDeadlines = (deadlines.data?.deadlines ?? []).filter((d) => d.isActive);
  const urgentDeadlines = activeDeadlines.filter((d) => d.status !== null && needsCustomerAction(d.status)).length;

  const externalHref = externalUiBase
    ? isWeclapp
      ? `${externalUiBase}/webapp/view/party/show.page?entityId=${encodeURIComponent(weclappId ?? customerId)}`
      : `${externalUiBase}/v4_6_release/services/system_io/Company/fv_co100_company.rails?company_recid=${encodeURIComponent(cwCompanyId ?? customerId)}`
    : null;
  const systemName = isWeclapp ? 'Weclapp' : 'ConnectWise';

  const sitesCard = (
    <InfoCard
      icon={<IconMapPin size={15} />}
      color="indigo"
      title="Standorte"
      count={siteRows.length || null}
      actions={
        siteRows.length > SITE_PREVIEW ? (
          <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setShowAllSites((v) => !v)}>
            {showAllSites ? 'Weniger' : `Alle ${siteRows.length} anzeigen`}
          </Button>
        ) : undefined
      }
      flush
    >
      {!isWeclapp && sitesLoading ? (
        <Center py="md">
          <Loader size="xs" />
        </Center>
      ) : siteRows.length === 0 ? (
        <Text size="sm" c="dimmed" className={classes.row}>
          Keine Standorte hinterlegt
        </Text>
      ) : (
        visibleSites.map((site) => (
          <div key={site.id} className={classes.row}>
            <Text size="sm" fw={500}>
              {site.name}
            </Text>
            {site.detail && (
              <Text size="xs" c="dimmed">
                {site.detail}
              </Text>
            )}
            {site.tags && site.tags.length > 0 && (
              <Group gap={4} mt={4}>
                {site.tags.map((tag) => (
                  <Badge key={tag} size="xs" variant="light" color={ADDRESS_TAG_COLORS[tag]}>
                    {ADDRESS_TAG_LABELS[tag]}
                  </Badge>
                ))}
              </Group>
            )}
          </div>
        ))
      )}
    </InfoCard>
  );

  const linksCard =
    !isWeclapp && (context?.referenceTicket || context?.weclappCustomerId) ? (
      <InfoCard icon={<IconLink size={15} />} title="Verknüpfungen" flush>
        {context.referenceTicket && (
          <div className={classes.row}>
            <Text size="xs" c="dimmed">
              Bezugsticket
            </Text>
            <Text size="sm">
              #{context.referenceTicket.id} — {context.referenceTicket.summary}
            </Text>
          </div>
        )}
        {context.weclappCustomerId && (
          <div className={classes.row}>
            <Text size="xs" c="dimmed">
              Weclapp-Kunde
            </Text>
            <Text size="sm">{context.weclappCustomerName || context.weclappCustomerId}</Text>
          </div>
        )}
      </InfoCard>
    ) : null;

  return (
    <>
      <Modal.Root
        opened={opened}
        onClose={onClose}
        // Über dem Ticket-Cockpit-Drawer (500): der Kundenname im Cockpit öffnet
        // dieses Modal — es muss davor liegen, nicht dahinter.
        zIndex={600}
        size={1040}
        fullScreen={isMobile}
        scrollAreaComponent={ScrollArea.Autosize}
        centered
      >
        <Modal.Overlay />
        <Modal.Content>
          {/*
            Die Reiter umschließen Kopf UND Inhalt: so steht die Reiterleiste im
            (klebenden) Modal-Kopf und bleibt beim Scrollen langer Listen sichtbar.
          */}
          <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
            <Modal.Header
              style={{
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 'var(--mantine-spacing-sm)',
                paddingBottom: 0,
              }}
            >
              <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <ThemeIcon size={42} radius="md" variant="light" color="cyberOrange">
                    <IconBuilding size={22} />
                  </ThemeIcon>
                  <div style={{ minWidth: 0 }}>
                    <Modal.Title
                      style={{
                        fontSize: 'var(--mantine-font-size-lg)',
                        fontWeight: 600,
                        lineHeight: 1.25,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {customerName}
                    </Modal.Title>
                    <Group gap={8} mt={4} wrap="wrap">
                      <Badge size="xs" variant="light" color={isWeclapp ? 'violet' : 'teal'}>
                        {systemName}
                      </Badge>
                      {care.data && <CustomerOpeningStatusBadge status={care.data.customer.openingStatus} size="xs" />}
                      {context?.customerNumber && (
                        <Text size="xs" c="dimmed">
                          Kd.-Nr. {context.customerNumber}
                        </Text>
                      )}
                      {externalHref && (
                        <Button
                          component="a"
                          href={externalHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="compact-xs"
                          variant="subtle"
                          color="gray"
                          leftSection={<IconExternalLink size={12} />}
                        >
                          In {systemName} öffnen
                        </Button>
                      )}
                    </Group>
                  </div>
                </Group>
                <Modal.CloseButton />
              </Group>

              <Tabs.List className={classes.tabList}>
                <Tabs.Tab value="overview" className={classes.tab} leftSection={<IconLayoutDashboard size={14} />}>
                  Übersicht
                </Tabs.Tab>
                <Tabs.Tab
                  value="contacts"
                  className={classes.tab}
                  leftSection={<IconUsers size={14} />}
                  rightSection={countBadge(contactCount)}
                >
                  Ansprechpartner
                </Tabs.Tab>
                <Tabs.Tab
                  value="contracts"
                  className={classes.tab}
                  leftSection={<IconFileCertificate size={14} />}
                  rightSection={
                    urgentDeadlines > 0 ? (
                      <Badge size="xs" variant="light" color="orange" radius="sm">
                        {urgentDeadlines} fällig
                      </Badge>
                    ) : (
                      countBadge(activeDeadlines.length)
                    )
                  }
                >
                  Verträge & Lizenzen
                </Tabs.Tab>
                <Tabs.Tab
                  value="tickets"
                  className={classes.tab}
                  leftSection={<IconTicket size={14} />}
                  rightSection={countBadge(ticketCount)}
                >
                  Tickets
                </Tabs.Tab>
                <Tabs.Tab
                  value="workflows"
                  className={classes.tab}
                  leftSection={<IconShoppingCart size={14} />}
                  rightSection={countBadge(workflows.length)}
                >
                  Bestellungen
                </Tabs.Tab>
                {localCustomerId && (
                  <Tabs.Tab value="material-history" className={classes.tab} leftSection={<IconPackage size={14} />}>
                    Material
                  </Tabs.Tab>
                )}
                {localCustomerId && (
                  <Tabs.Tab value="dns-zones" className={classes.tab} leftSection={<IconWorldWww size={14} />}>
                    DNS-Zonen
                  </Tabs.Tab>
                )}
                {localCustomerId && (
                  <Tabs.Tab value="domains" className={classes.tab} leftSection={<IconWorld size={14} />}>
                    Domains
                  </Tabs.Tab>
                )}
              </Tabs.List>
            </Modal.Header>

            <Modal.Body
              style={{
                paddingTop: 'var(--mantine-spacing-md)',
                // Feste Mindesthöhe: beim Wechsel zwischen vollen und leeren Reitern springt das Fenster nicht.
                minHeight: isMobile ? undefined : 'min(620px, calc(100dvh - 16rem))',
              }}
            >
              {/* ── Übersicht ─────────────────────────────────────── */}
              <Tabs.Panel value="overview">
                {care.isLoading ? (
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                ) : care.data ? (
                  <Stack gap="md">
                    <ServiceNotesCard ident={ident} />
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" verticalSpacing="md">
                      <Stack gap="md">
                        <OpeningHoursCard ident={ident} />
                        <ContractCareCard ident={ident} />
                        {linksCard}
                      </Stack>
                      <Stack gap="md">
                        <PortfolioCard ident={ident} />
                        {sitesCard}
                      </Stack>
                    </SimpleGrid>
                  </Stack>
                ) : (
                  // Kennt die lokale DB den Kunden (noch) nicht, gibt es nur, was das CRM liefert.
                  <Stack gap="md">
                    <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                      Dieser Kunde ist im ServiceHub noch nicht angelegt. Besonderheiten, Vertrag und
                      Ansprechpartner lassen sich erst pflegen, wenn er synchronisiert ist.
                    </Alert>
                    {sitesCard}
                    {linksCard}
                  </Stack>
                )}
              </Tabs.Panel>

              {/* ── Ansprechpartner ───────────────────────────────── */}
              <Tabs.Panel value="contacts">
                <CustomerContactsPanel ident={ident} fallbackContacts={contacts} />
              </Tabs.Panel>

              {/* ── Verträge & Lizenzen (Portal-Modul „Fristen") ──── */}
              <Tabs.Panel value="contracts">
                <CustomerDeadlinesSection
                  weclappCustomerId={weclappId}
                  cwCompanyId={cwCompanyId}
                  localCustomerId={localCustomerId}
                  withTitle={false}
                  fallback={
                    <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                      Dieser Kunde ist im ServiceHub noch nicht angelegt. Verträge und Lizenzen lassen sich erst
                      erfassen, wenn er synchronisiert ist.
                    </Alert>
                  }
                />
              </Tabs.Panel>

              {/* ── Tickets ───────────────────────────────────────── */}
              <Tabs.Panel value="tickets">
                {ticketsLoading ? (
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                ) : loadedTicketCount === 0 ? (
                  <InfoEmpty icon={<IconTicket size={20} />}>Keine Tickets für diesen Kunden</InfoEmpty>
                ) : (
                  <Stack gap="sm">
                    <Group justify="space-between" gap="sm" wrap="wrap">
                      {loadedTicketCount >= TICKET_SEARCH_FROM ? (
                        <TextInput
                          size="xs"
                          w={280}
                          leftSection={<IconSearch size={14} />}
                          placeholder="Betreff oder Ticketnummer"
                          aria-label="Tickets durchsuchen"
                          value={ticketSearch}
                          onChange={(e) => setTicketSearch(e.currentTarget.value)}
                        />
                      ) : (
                        <span />
                      )}
                      <Text size="xs" c="dimmed">
                        {ticketQuery
                          ? `${shownTicketCount} von ${loadedTicketCount} Tickets`
                          : ticketCount > loadedTicketCount
                            ? `${loadedTicketCount} von ${ticketCount} Tickets geladen`
                            : `${ticketCount} Tickets`}
                      </Text>
                    </Group>
                    {shownTicketCount === 0 ? (
                      <Text size="sm" c="dimmed" ta="center" py="lg">
                        Kein Ticket passt zur Suche.
                      </Text>
                    ) : (
                      <ListSurface>
                        {isWeclapp
                          ? shownWeclappTickets.map((t) => (
                              <WeclappTicketRow key={t.id} ticket={t} onClick={() => setWeclappCockpitId(t.id)} />
                            ))
                          : shownCwTickets.map((t) => (
                              <CwTicketRow
                                key={t.id}
                                ticket={t}
                                onClick={() => {
                                  openCockpit(t.id);
                                  onClose();
                                }}
                              />
                            ))}
                      </ListSurface>
                    )}
                  </Stack>
                )}
              </Tabs.Panel>

              {/* ── Bestellungen / Workflows ──────────────────────── */}
              <Tabs.Panel value="workflows">
                {workflowsLoading ? (
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                ) : workflows.length === 0 ? (
                  <InfoEmpty icon={<IconShoppingCart size={20} />}>
                    Keine Bestellungen, Angebots- oder Garantieanfragen für diesen Kunden
                  </InfoEmpty>
                ) : (
                  <ListSurface>
                    {workflows.map((wf) => (
                      <div
                        key={wf.id}
                        className={`${classes.row} ${classes.clickable}`}
                        {...rowActivation(() => {
                          navigate(`${KIND_ROUTES[wf.kind]}/${wf.id}`);
                          onClose();
                        })}
                      >
                        <Group justify="space-between" wrap="nowrap" gap="sm">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500}>
                              {KIND_LABELS[wf.kind]}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {[formatDate(wf.updatedAt), wf.cwTicketId ? `CW-Ticket #${wf.cwTicketId}` : null]
                                .filter(Boolean)
                                .join(' · ')}
                            </Text>
                          </div>
                          <Badge size="xs" variant="light" color={STATUS_COLORS[wf.status]} style={{ flexShrink: 0 }}>
                            {STATUS_LABELS[wf.status]}
                          </Badge>
                        </Group>
                      </div>
                    ))}
                  </ListSurface>
                )}
              </Tabs.Panel>

              {/* ── Material-Historie ─────────────────────────────── */}
              {localCustomerId && (
                <Tabs.Panel value="material-history">
                  <MaterialHistoryTab customerId={localCustomerId} active={activeTab === 'material-history'} />
                </Tabs.Panel>
              )}

              {/* ── DNS-Zonen (v1.408.0) ──────────────────────────── */}
              {localCustomerId && (
                <Tabs.Panel value="dns-zones">
                  <CustomerDnsZonesTab customerId={localCustomerId} active={activeTab === 'dns-zones'} />
                </Tabs.Panel>
              )}

              {/* ── Domains aus AutoDNS (v1.409.0) ─────────────────── */}
              {localCustomerId && (
                <Tabs.Panel value="domains">
                  <CustomerDomainsTab customerId={localCustomerId} active={activeTab === 'domains'} />
                </Tabs.Panel>
              )}
            </Modal.Body>
          </Tabs>
        </Modal.Content>
      </Modal.Root>

      {/* Weclapp Ticket Cockpit — außerhalb des Modals um Modal-Nesting zu vermeiden */}
      {weclappCockpitId && (
        <WeclappTicketCockpit
          ticketId={weclappCockpitId}
          onClose={() => setWeclappCockpitId(null)}
          zIndex={650}
        />
      )}
    </>
  );
}
